<template>
  <TopGradientBlockLayout>
    <div class="text-xl font-bold text-text leading-tight mb-11">{{ $t('title') }}</div>
    <div class="space-y-8">

        <div class="leading-tight bg-red bg-opacity-90 border border-red rounded-lg p-10">
          <div class="text-md font-bold text-white mb-6 rounded-full">{{ $t('warningTitle') }}</div>
          <ul class="text-sm text-white space-y-2 ml-6 list-disc list-inside">
            <li>{{ $t('warning1') }}</li>
            <li>{{ $t('warning2') }}</li>
            <li>{{ $t('warning3') }}</li>
          </ul>
        </div>

        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6">
          <PasswordInput v-model.trim="password"
                         :password-reveal="true"
                         :label="$t('passwordLabel')"
                         :disabled="busy"
                         :error="!!passwordError"
                         @focusin="clearError">
            <template slot="errors">
              <div class="error" v-if="passwordError">
                {{ passwordError }}
              </div>
            </template>
          </PasswordInput>
          <div class="text-xs text-text-ternary">{{ passwordHint }}</div>
        </div>

      <div class="flex items-center gap-4">
        <BaseButton theme="red"
                    class="w-auto"
                    :loading="deleting"
                    :disabled="!password || busy"
                    @click="handleDeleteAccount">
          {{ $t('deleteButton') }}
        </BaseButton>
      </div>

      <div v-if="linkedProviders.length">
        <div class="flex flex-wrap justify-start pt-2 mb-6">
          <div class="w-full bg-line h-px"></div>
          <div class="text-text-ternary text-2sm px-8 bg-block transform -translate-y-1/2">
            {{ $t('orConfirmWithProvider') }}
          </div>
        </div>
        <div class="flex flex-wrap -mx-2">
          <BaseButton v-for="provider in linkedProviders"
                      :key="provider"
                      theme="red-outline"
                      class="w-auto mx-2 mb-4"
                      :loading="deletingProvider === provider"
                      :disabled="busy"
                      @click="handleProviderDelete(provider)">
            <font-awesome-icon class="mr-3" :icon="['fab', provider]"/>
            {{ $t('deleteVia', {provider: providerLabel(provider)}) }}
          </BaseButton>
        </div>
      </div>
    </div>
  </TopGradientBlockLayout>
</template>

<script>
import {mapActions, mapState} from "vuex";
import TopGradientBlockLayout from "@/components/layouts/cabinet/TopGradientBlockLayout";
import PasswordInput from "@/components/UI/BaseInput/PasswordInput";
import BaseButton from "@/components/UI/BaseButton";

export default {
  name: "AccountDeletion",
  components: {
    TopGradientBlockLayout,
    PasswordInput,
    BaseButton,
  },
  data() {
    return {
      password: '',
      deleting: false,
      deletingProvider: null,
      passwordError: null,
    }
  },
  computed: {
    ...mapState('cabinet/user', ['user_info']),
    busy() {
      return this.deleting || !!this.deletingProvider;
    },
    linkedProviders() {
      const accounts = (this.user_info && this.user_info.data && this.user_info.data.auth_accounts) || {};
      return ['google', 'apple'].filter(name => !!accounts[name]);
    },
    passwordHint() {
      return this.linkedProviders.length ? this.$t('passwordTipOauth') : this.$t('passwordTip');
    }
  },
  methods: {
    ...mapActions('cabinet/user', ['deleteAccount', 'startProviderDeletion']),
    providerLabel(provider) {
      const names = {google: 'Google', apple: 'Apple'};
      return names[provider] || provider;
    },
    clearError() {
      this.passwordError = null;
    },
    async handleDeleteAccount() {
      if (!this.password) {
        this.passwordError = this.$t('errors.passwordRequired');
        return;
      }

      try {
        const confirmed = confirm(this.$t('confirmMessage'));
        if (!confirmed) {
          return;
        }

        this.deleting = true;
        this.passwordError = null;

        const res = await this.$store.dispatch('cabinet/user/deleteAccount', {password: this.password});

        if (res.body.success) {
          this.$toasted.success(this.$t('successMessage'));
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        } else {
          this.passwordError = res.body.message || this.$t('errors.deleteFailed');
          this.$toasted.error(this.$t('errors.deleteFailed'));
        }
      } catch (err) {
        console.error(err);
        this.passwordError = this.$t('errors.deleteFailed');
        this.$toasted.error(this.$t('errors.deleteFailed'));
      } finally {
        this.deleting = false;
      }
    },
    async handleProviderDelete(provider) {
      try {
        const confirmed = confirm(this.$t('confirmMessage'));
        if (!confirmed) {
          return;
        }

        this.deletingProvider = provider;
        const res = await this.startProviderDeletion(provider);
        if (res.body.success && res.body.data) {
          window.location.href = res.body.data;
          return;
        }
        this.$toasted.error(res.body.message || this.$t('errors.providerFailed'));
      } catch (err) {
        console.error(err);
        this.$toasted.error(this.$t('errors.providerFailed'));
      } finally {
        this.deletingProvider = null;
      }
    },
  },
  created() {
    const error = this.$route.query.error;
    if (!error) {
      return;
    }
    const messages = {
      expired: this.$t('errors.expired'),
      delete_failed: this.$t('errors.deleteFailed'),
      provider: this.$t('errors.providerFailed'),
    };
    this.$toasted.error(messages[error] || this.$t('errors.deleteFailed'));
    const query = Object.assign({}, this.$route.query);
    delete query.error;
    this.$router.replace({query});
  },
  i18n: {
    messages: {
      en: {
        title: 'Delete Account',
        warningTitle: 'Warning: This action cannot be undone',
        warning1: 'Your account will be permanently deactivated',
        warning2: 'All your personal data will be anonymized',
        warning3: 'You will lose access to all your workspaces and data',
        passwordLabel: 'Enter your password to confirm',
        passwordTip: 'You must enter your current password to delete your account',
        passwordTipOauth: 'If you signed in with Google or Apple, the password may have been generated automatically — confirm deletion with a button below.',
        orConfirmWithProvider: 'or confirm with a linked account',
        deleteVia: 'Delete with {provider}',
        deleteButton: 'Delete Account',
        confirmMessage: 'Are you sure you want to delete your account? This action cannot be undone.',
        successMessage: 'Your account has been deleted successfully',
        errors: {
          passwordRequired: 'Password is required',
          deleteFailed: 'Failed to delete account. Please check your password and try again.',
          providerFailed: 'Could not confirm deletion through this account. Please try again.',
          expired: 'Confirmation expired. Please start deletion again.'
        }
      },
      ru: {
        title: 'Удаление аккаунта',
        warningTitle: 'Внимание: это действие необратимо',
        warning1: 'Ваш аккаунт будет удален.',
        warning2: 'Все ваши персональные данные будут анонимизированы.',
        warning3: 'Вы потеряете доступ ко всем вашим пространствам и данным.',
        passwordLabel: 'Введите пароль для подтверждения',
        passwordTip: 'Вы должны ввести текущий пароль для удаления аккаунта',
        passwordTipOauth: 'Если вы входили через Google или Apple, пароль мог быть создан автоматически — подтвердите удаление кнопкой ниже.',
        orConfirmWithProvider: 'или подтвердить через связанный аккаунт',
        deleteVia: 'Удалить через {provider}',
        deleteButton: 'Удалить аккаунт',
        confirmMessage: 'Вы уверены, что хотите удалить свой аккаунт? Это действие необратимо.',
        successMessage: 'Ваш аккаунт успешно удален',
        errors: {
          passwordRequired: 'Пароль обязателен',
          deleteFailed: 'Не удалось удалить аккаунт. Проверьте пароль и попробуйте снова.',
          providerFailed: 'Не удалось подтвердить удаление через этот аккаунт. Попробуйте ещё раз.',
          expired: 'Подтверждение устарело. Начните удаление заново.'
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
</style>
